import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowRightIcon, CheckCircleIcon, PlantIcon, FlaskIcon, DropIcon, SprayBottleIcon,
  PackageIcon, MountainsIcon, MapPinIcon, PhoneIcon, EnvelopeSimpleIcon, ClockIcon,
  FacebookLogoIcon, InstagramLogoIcon, WhatsappLogoIcon, LinkedinLogoIcon,
  ListIcon, XIcon, TargetIcon, EyeIcon, HeartIcon, LeafIcon,
  StorefrontIcon, UsersThreeIcon, MedalIcon, TrophyIcon, PlayIcon, QuotesIcon,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import CustomCursor from "@/components/CustomCursor";
import AmbientLights from "@/components/AmbientLights";
import SeedDNAScroll from "@/components/SeedDNAScroll";
import NavHeader from "@/components/ui/nav-header";
import {
  useLenis, useTilt3D, useMagnetic, useMouseParallax, useScrollZoom,
} from "@/lib/landingEffects";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const ICON_MAP = { plant: PlantIcon, flask: FlaskIcon, drop: DropIcon, spray: SprayBottleIcon, mountains: MountainsIcon, package: PackageIcon };

// ============================================================
// LANDING AGROPORT — Página pública de entrada al ERP
// ============================================================
// • Solo idioma español
// • Solo login (no registro público)
// • Inspirado en Agrion (pixydrops) + identidad AGROPORT
// • Tailwind + Phosphor icons + Unsplash photos
// ============================================================

// Photos (Unsplash — estables, sin descarga)
const PHOTO = {
  hero: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=80", // campo amarillo dorado al atardecer
  about1: "https://images.unsplash.com/photo-1592982537447-7440770cbfc9?auto=format&fit=crop&w=1200&q=80", // mano con semilla
  about2: "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?auto=format&fit=crop&w=1200&q=80", // productor con cultivo
  history: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?auto=format&fit=crop&w=1400&q=80", // semillas en mano
  prod_seeds: "https://images.unsplash.com/photo-1530507629858-e3759c1fa5f5?auto=format&fit=crop&w=800&q=80",
  prod_chem: "https://images.unsplash.com/photo-1620120966883-d977b57a96ec?auto=format&fit=crop&w=800&q=80",
  prod_fert: "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=800&q=80",
  prod_pump: "https://images.unsplash.com/photo-1599507593362-3d10c2706f72?auto=format&fit=crop&w=800&q=80",
  prod_enm: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=800&q=80",
  prod_special: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=800&q=80",
  gallery: [
    "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1592982537447-7440770cbfc9?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1574943320219-5630bb4c5778?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1601593768799-76d3f1cdf8f9?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?auto=format&fit=crop&w=800&q=80",
  ],
};

// ------------------------------------------------------------
// Hook: revela elementos al entrar al viewport.
// Soporta variantes (data-reveal="fade-up|fade-left|scale-in|..."),
// delay personalizado (data-reveal-delay="200") y stagger automático
// para hijos directos dentro de [data-reveal-stagger].
// Usa MutationObserver para detectar elementos que aparecen DESPUÉS
// del mount (ej: testimoniales que llegan async desde cfg backend).
// ------------------------------------------------------------
function useReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -20px 0px" },
    );

    const inViewport = (el) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    };

    const checkVisibility = () => {
      document.querySelectorAll("[data-reveal]:not(.revealed)").forEach((el) => {
        if (inViewport(el)) {
          el.classList.add("revealed");
          io.unobserve(el);
        }
      });
    };

    const observeNewReveals = () => {
      document.querySelectorAll("[data-reveal-stagger]").forEach((g) => {
        g.querySelectorAll(":scope > [data-reveal]").forEach((child, idx) => {
          if (!child.style.getPropertyValue("--stagger-index")) {
            child.style.setProperty("--stagger-index", String(idx));
          }
        });
      });
      document.querySelectorAll("[data-reveal]:not([data-reveal-observed])").forEach((el) => {
        const d = el.getAttribute("data-reveal-delay");
        if (d) el.style.setProperty("--reveal-delay", `${d}ms`);
        const dur = el.getAttribute("data-reveal-duration");
        if (dur) el.style.setProperty("--reveal-dur", `${dur}ms`);
        el.setAttribute("data-reveal-observed", "1");
        io.observe(el);
        // Reveal inmediato si ya está visible (caso async render del cfg)
        if (inViewport(el)) {
          el.classList.add("revealed");
          io.unobserve(el);
        }
      });
    };

    observeNewReveals();

    // Re-escanear DOM dinámico (cfg async)
    const mo = new MutationObserver(() => observeNewReveals());
    mo.observe(document.body, { childList: true, subtree: true });

    // Safety net: en cada scroll revisar elementos visibles que el IO no atrapó
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(checkVisibility);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      io.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
}

// ------------------------------------------------------------
// Barra de progreso de scroll (decorativa, fina, arriba)
// ------------------------------------------------------------
function ScrollProgress() {
  useEffect(() => {
    const bar = document.querySelector(".scroll-progress");
    if (!bar) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = document.documentElement;
        const pct = h.scrollTop / Math.max(h.scrollHeight - h.clientHeight, 1);
        bar.style.transform = `scaleX(${pct})`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <div className="scroll-progress" aria-hidden />;
}

// ------------------------------------------------------------
// Parallax simple: aplica un translateY relativo al scroll local del elemento
// ------------------------------------------------------------
function useParallax(strength = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // -1 cuando está abajo, +1 cuando está arriba
        const progress = (vh / 2 - (rect.top + rect.height / 2)) / vh;
        el.style.transform = `translate3d(0, ${progress * strength * 100}px, 0)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [strength]);
  return ref;
}

// ------------------------------------------------------------
// CountUp animado para indicadores
// ------------------------------------------------------------
function CountUp({ to, suffix = "", duration = 1600 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let started = false;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started) {
        started = true;
        const start = performance.now();
        const step = (t) => {
          const p = Math.min((t - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(eased * to));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val.toLocaleString("es-HN")}{suffix}</span>;
}

// ============================================================
// SCROLL TO TOP — Botón flotante que aparece al scrollear
// Permite al usuario regresar al inicio rápido desde cualquier punto.
// ============================================================
function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onS = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", onS, { passive: true });
    onS();
    return () => window.removeEventListener("scroll", onS);
  }, []);
  const handleClick = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="scroll-to-top-btn"
      aria-label="Volver arriba"
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-agro-green hover:bg-agro-green-hover text-black shadow-[0_10px_30px_-8px_rgba(2,191,68,0.6)] flex items-center justify-center transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <ArrowRightIcon size={18} weight="bold" style={{ transform: "rotate(-90deg)" }} />
    </button>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useReveal();
  useLenis();
  const [cfg, setCfg] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    api.get("/landing/settings").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
    document.body.classList.add("cursor-active");
    return () => document.body.classList.remove("cursor-active");
  }, []);

  const handleAccess = () => {
    if (user) navigate("/dashboard");
    else navigate("/login");
  };

  return (
    <div className="relative bg-[#0A0F0B] text-text-primary min-h-screen [overflow-x:clip] font-body" data-testid="landing-page">
      <AmbientLights />
      <ScrollProgress />
      <CustomCursor />
      <div className="relative z-10">
        <NavHeader
          onAccess={handleAccess}
          userLogged={!!user}
          items={cfg?.nav?.items}
          ctaLabel={cfg?.nav?.cta_label}
          ctaLabelLogged={cfg?.nav?.cta_label_logged}
        />
        <Hero cfg={cfg} onAccess={handleAccess} userLogged={!!user} />
        <SeedDNAScroll cfg={cfg?.corn} />
        <About cfg={cfg} onOpenVideo={() => setVideoOpen(true)} />
        <History />
        <MVV />
        <Products cfg={cfg} />
        <Coverage />
        <Testimonials cfg={cfg} />
        <Stats cfg={cfg} />
        <Gallery cfg={cfg} />
        <Contact cfg={cfg} />
        <Footer cfg={cfg} />
      </div>
      {videoOpen && <VideoModal url={cfg?.about?.video_url} onClose={() => setVideoOpen(false)} />}
      <ScrollToTopButton />
    </div>
  );
}

// === Video Modal (YouTube embed o video URL directo) ===
function VideoModal({ url, onClose }) {
  const yt = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  const embed = yt ? `https://www.youtube.com/embed/${yt[1]}?autoplay=1` : url;
  const fallback = "https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1";
  const vm = useModalForm({ onClose });
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl p-4" {...vm.overlayProps} data-testid="video-modal">
      <button aria-label="Cerrar" onClick={onClose} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-cursor="hover">
        <XIcon size={20} weight="bold" />
      </button>
      <div className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {embed && embed.includes("youtube") ? (
          <iframe src={embed} title="Video AGROPORT" className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
        ) : embed ? (
          <video src={embed} autoPlay controls className="w-full h-full bg-black" />
        ) : (
          <iframe src={fallback} title="Video" className="w-full h-full" allow="autoplay" allowFullScreen />
        )}
      </div>
    </div>
  );
}

// ============================================================
// NAVBAR sticky
// ============================================================
function NavBar({ onAccess, navOpen, setNavOpen, userLogged }) {
  const [scrolled, setScrolled] = useState(false);
  const navCtaRef = useMagnetic({ strength: 0.18 });
  useEffect(() => {
    const onS = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onS, { passive: true });
    return () => window.removeEventListener("scroll", onS);
  }, []);
  const links = [
    { href: "#about", label: "Nosotros" },
    { href: "#productos", label: "Productos" },
    { href: "#cobertura", label: "Cobertura" },
    { href: "#galeria", label: "Galería" },
    { href: "#contacto", label: "Contacto" },
  ];
  return (
    <header
      data-testid="landing-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-scrolled py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2 flex-shrink-0" data-testid="landing-logo">
          <img src="/agroport-logo-green.png" alt="AGROPORT" className="h-10 md:h-12 w-auto" />
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm font-medium text-white/75 hover:text-agro-green transition-colors relative group">
              {l.label}
              <span className="absolute -bottom-1 left-0 right-0 mx-auto w-0 group-hover:w-full h-[2px] bg-agro-green transition-all duration-300" />
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            ref={navCtaRef}
            onClick={onAccess}
            data-testid="navbar-login-btn"
            className="magnetic-btn magnetic-btn-shimmer hidden sm:inline-flex items-center gap-2 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
          >
            {userLogged ? "Ir al ERP" : "Iniciar Sesión"}
            <ArrowRightIcon size={14} weight="bold" />
          </button>
          <button
            onClick={() => setNavOpen(!navOpen)}
            className="lg:hidden p-2 rounded-md text-white hover:bg-white/5"
            aria-label="Menú"
            data-testid="navbar-mobile-toggle"
          >
            {navOpen ? <XIcon size={22} /> : <ListIcon size={22} />}
          </button>
        </div>
      </div>
      {/* Mobile nav */}
      {navOpen && (
        <div className="lg:hidden absolute left-0 right-0 top-full bg-[#0A0F0B]/98 backdrop-blur-xl border-t border-white/10 px-4 py-4 shadow-2xl z-[60]">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setNavOpen(false)}
              className="block py-3 text-base text-white/85 border-b border-white/5 last:border-0"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => { setNavOpen(false); onAccess(); }}
            className="w-full mt-4 bg-agro-green text-black font-semibold rounded-full py-3 text-sm"
          >
            {userLogged ? "Ir al ERP" : "Iniciar Sesión"}
          </button>
        </div>
      )}
    </header>
  );
}

// ============================================================
// HERO
// ============================================================
function Hero({ cfg, onAccess, userLogged }) {
  const hero = cfg?.hero || {};
  const bg = hero.background_image || PHOTO.hero;
  const bgVideo = hero.background_video;
  // background_type: "image" | "video". Fallback: si hay video pero no tipo definido → video.
  const bgType = hero.background_type || (bgVideo ? "video" : "image");
  const useVideo = bgType === "video" && !!bgVideo;
  // overlay_opacity: 0-100 (porcentaje de oscurecimiento uniforme). Default 15%.
  const overlayOpacity = Math.max(0, Math.min(100, Number(hero.overlay_opacity ?? 15))) / 100;
  const bgRef = useScrollZoom({ maxZoom: 0.1 });
  const parallaxRef = useMouseParallax({ strength: 22 });
  const tiltRef = useTilt3D({ max: 10, scale: 1.025, glow: true });
  const ctaPrimaryRef = useMagnetic({ strength: 0.22 });
  const ctaSecondaryRef = useMagnetic({ strength: 0.18 });
  // Video ready state → controla fade-in del video para evitar flash inicial y en cada loop.
  const [videoReady, setVideoReady] = useState(false);
  return (
    <section id="top" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
      {/* Background — video o imagen con mouse-parallax + scroll-zoom */}
      <div ref={bgRef} className="absolute inset-0 will-change-transform" style={{ transformOrigin: "center center" }}>
        <div ref={parallaxRef} className="absolute inset-0 will-change-transform">
          {useVideo ? (
            <>
              {/* Poster estático detrás del video: siempre visible, tapa cualquier flash de loop */}
              <img
                src={bg}
                alt=""
                className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
                draggable={false}
              />
              <video
                key={bgVideo}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                onCanPlay={() => setVideoReady(true)}
                onLoadedData={() => setVideoReady(true)}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-100" : "opacity-0"}`}
                style={{ willChange: "opacity" }}
                data-testid="hero-bg-video"
              >
                {/* Si el archivo es local (.mp4), ofrecemos también variante .webm para navegadores sin H.264 */}
                {bgVideo.startsWith("/videos/") && bgVideo.endsWith(".mp4") && (
                  <source src={bgVideo.replace(/\.mp4$/, ".webm")} type="video/webm" />
                )}
                <source src={bgVideo} type={bgVideo.toLowerCase().endsWith(".webm") ? "video/webm" : bgVideo.toLowerCase().endsWith(".mov") ? "video/quicktime" : "video/mp4"} />
              </video>
            </>
          ) : (
            <img src={bg} alt="Campo agrícola" className="w-full h-full object-cover" data-testid="hero-bg-image" />
          )}
        </div>
        {/* Overlay sutil configurable desde CMS (para legibilidad del texto blanco) */}
        {overlayOpacity > 0 && (
          <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: overlayOpacity }} />
        )}
        {/* Gradientes laterales/inferiores para mantener contraste del contenido */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F0B]/70 via-[#0A0F0B]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0B] via-transparent to-transparent" />
      </div>

      <div className="absolute inset-0 pointer-events-none opacity-30"
           style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(2,191,68,0.15), transparent 50%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7" data-reveal="fade-right" data-reveal-duration="1100">
          <div className="inline-flex items-center gap-2 bg-agro-green/10 border border-agro-green/30 rounded-full px-3 py-1.5 mb-6">
            <LeafIcon size={14} weight="fill" className="text-agro-green" />
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-agro-green">{hero.badge || "Honduras · Agro Líder"}</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] text-white mb-3">
            {hero.title_pre || "Impulsando la"} <span className="text-agro-green text-glow-green">{hero.title_highlight || "agricultura de Honduras"}</span> {hero.title_post || "con innovación, calidad y confianza."}
          </h1>
          <span className="ag-swoosh mb-6" />
          <p className="text-lg md:text-xl text-white/75 max-w-2xl mb-10 mt-4 leading-relaxed">
            {hero.subtitle || "Líderes en soluciones agrícolas, semillas, agroquímicos y fertilizantes para productores de todo el país."}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              ref={ctaPrimaryRef}
              onClick={onAccess}
              data-testid="hero-cta-login"
              className="magnetic-btn magnetic-btn-shimmer inline-flex items-center gap-2.5 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-full px-7 py-3.5 text-sm md:text-base transition-colors shadow-[0_8px_30px_-8px_rgba(2,191,68,0.6)]"
            >
              {userLogged ? "Ir al ERP" : (hero.cta_primary || "Iniciar Sesión")}
              <ArrowRightIcon size={16} weight="bold" />
            </button>
            <a
              ref={ctaSecondaryRef}
              href="#about"
              data-testid="hero-cta-more"
              className="magnetic-btn magnetic-btn-yellow inline-flex items-center gap-2.5 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-white/30 text-white font-semibold rounded-full px-7 py-3.5 text-sm md:text-base transition-colors"
            >
              {hero.cta_secondary || "Conocer Más"}
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
            {(cfg?.stats || []).slice(0, 3).map((s) => (
              <div key={s.label} className="border-l-2 border-agro-green/50 pl-3">
                <div className="font-heading text-2xl md:text-3xl font-bold text-white">{s.suffix === "+" ? "+" : ""}{s.value.toLocaleString("es-HN")}{s.suffix !== "+" ? s.suffix : ""}</div>
                <div className="text-[11px] text-white/60 uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 hidden lg:flex justify-end" data-reveal="fade-left" data-reveal-delay="250" data-reveal-duration="1100">
          <div className="relative">
            <div className="absolute inset-0 bg-agro-green/20 blur-3xl rounded-full" />
            <div ref={tiltRef} className="tilt-card glass-premium relative rounded-3xl p-6 w-[380px]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-agro-green/15 flex items-center justify-center">
                  <PlantIcon size={20} weight="fill" className="text-agro-green" />
                </div>
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider">Soluciones agrícolas</div>
                  <div className="text-sm font-semibold text-white">Para cada etapa del cultivo</div>
                </div>
              </div>
              <ul className="space-y-3">
                {(cfg?.about?.bullets || ["Semillas certificadas de alto rendimiento", "Asesoría técnica personalizada", "Productos de primera línea", "Distribución a nivel nacional"]).map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-white/80">
                    <CheckCircleIcon size={16} weight="fill" className="text-agro-green flex-shrink-0 mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Sucursales</div>
                    <div className="text-sm text-white font-medium">Choluteca · San Marcos</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-agro-yellow/15 flex items-center justify-center">
                    <MapPinIcon size={16} weight="fill" className="text-agro-yellow" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 animate-bounce">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Desliza</div>
        <div className="w-px h-8 bg-gradient-to-b from-white/50 to-transparent" />
      </div>
    </section>
  );
}

// ============================================================
// ABOUT
// ============================================================
function About({ cfg, onOpenVideo }) {
  const a = cfg?.about || {};
  return (
    <section id="about" className="relative py-24 md:py-32 bg-[#0A130C]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 relative" data-reveal="fade-right" data-reveal-duration="1000">
          <div className="relative">
            <img src={a.image1 || PHOTO.about1} alt="Productor con semillas"
                 className="rounded-3xl w-full aspect-[4/5] object-cover shadow-2xl" />
            <div className="hidden md:block absolute -bottom-6 -right-6 w-2/3 aspect-square rounded-3xl overflow-hidden border-4 border-[#0A130C] shadow-2xl">
              <img src={a.image2 || PHOTO.about2} alt="Cultivo" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-4 -left-4 bg-agro-green text-black rounded-2xl px-5 py-4 shadow-xl">
              <div className="font-heading text-3xl font-bold leading-none">+15</div>
              <div className="text-xs font-medium uppercase tracking-wider mt-1">Años</div>
            </div>
            {/* Play button overlay */}
            <button
              onClick={onOpenVideo}
              data-testid="about-play-btn"
              data-cursor="hover"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-agro-yellow text-black flex items-center justify-center shadow-2xl group hover:scale-110 transition-transform"
            >
              <span className="absolute inset-0 rounded-full border-2 border-agro-yellow animate-ping opacity-40" />
              <span className="absolute -inset-3 rounded-full border border-agro-yellow/30" />
              <PlayIcon size={28} weight="fill" className="ml-1" />
            </button>
          </div>
        </div>
        <div className="lg:col-span-6" data-reveal="fade-left" data-reveal-delay="150" data-reveal-duration="1000">
          <SectionLabel icon={LeafIcon}>{a.label || "Quiénes Somos"}</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
            {a.title || "Cosechando confianza junto a los productores de Honduras."}
          </h2>
          <span className="ag-swoosh mb-6" />
          <p className="text-base text-white/75 leading-relaxed mb-4 mt-4">
            {a.paragraph1 || "En AGROPORT trabajamos hombro a hombro con productores, fincas y cooperativas..."}
          </p>
          <p className="text-base text-white/65 leading-relaxed mb-8">
            {a.paragraph2 || "Distribuimos productos de marcas líderes mundiales..."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(a.bullets || ["Asesoría agronómica profesional", "Productos certificados internacionales", "Distribución directa al productor", "Atención personalizada en campo"]).map((t) => (
              <div key={t} className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                <CheckCircleIcon size={20} weight="fill" className="text-agro-green flex-shrink-0" />
                <span className="text-sm text-white/85">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// HISTORY (timeline)
// ============================================================
function History() {
  const milestones = [
    { year: "2009", title: "Nuestros orígenes", text: "AGROPORT nace como empresa familiar en San Marcos de Colón, atendiendo a productores locales." },
    { year: "2013", title: "Primera expansión", text: "Apertura de la sucursal en Choluteca, ampliando nuestra red comercial al sur del país." },
    { year: "2017", title: "Alianzas estratégicas", text: "Firmamos representaciones con marcas internacionales de semillas, agroquímicos y fertilizantes." },
    { year: "2020", title: "Cobertura nacional", text: "Logramos distribución en todos los departamentos de Honduras con un equipo de RTC propios." },
    { year: "2026", title: "Transformación digital", text: "Lanzamos nuestro ERP propio para llevar trazabilidad y eficiencia a cada operación." },
  ];
  return (
    <section className="relative py-24 md:py-32 bg-[#0A0F0B]" data-testid="history-section">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <img src={PHOTO.history} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F0B] via-[#0A0F0B]/95 to-[#0A0F0B]/85" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16" data-reveal>
          <SectionLabel icon={ClockIcon} center>Nuestra Historia</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl mx-auto">
            De empresa familiar a <span className="text-agro-green">distribuidor nacional</span>
          </h2>
        </div>
        <div className="relative" data-reveal-stagger>
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-agro-green/0 via-agro-green/40 to-agro-green/0 md:-translate-x-px" />
          {milestones.map((m, i) => (
            <div
              key={m.year}
              data-reveal={i % 2 === 0 ? "fade-right" : "fade-left"}
              className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-12 mb-10 last:mb-0 ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}
            >
              <div className={`md:text-right ${i % 2 === 1 ? "md:order-2 md:text-left" : ""}`}>
                <div className="card-3d bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-agro-green/40 rounded-2xl p-6 transition-all duration-500 inline-block">
                  <div className="font-heading text-3xl font-bold text-agro-green mb-2">{m.year}</div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">{m.title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{m.text}</p>
                </div>
              </div>
              <div className={`hidden md:block ${i % 2 === 1 ? "md:order-1" : ""}`} />
              <div className="absolute left-4 md:left-1/2 top-6 w-3 h-3 rounded-full bg-agro-green ring-4 ring-[#0A0F0B] md:-translate-x-1/2" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// MVV
// ============================================================
function MVV() {
  const items = [
    {
      icon: TargetIcon,
      label: "Misión",
      title: "Impulsar la productividad agrícola",
      text: "Ofrecer soluciones integrales, productos de calidad y asesoría técnica que potencien el rendimiento de cada productor hondureño.",
      accent: "green",
    },
    {
      icon: EyeIcon,
      label: "Visión",
      title: "Ser el aliado #1 del agro hondureño",
      text: "Convertirnos en la marca de mayor confianza y referencia para productores y cooperativas, llevando innovación a cada cultivo.",
      accent: "yellow",
    },
    {
      icon: HeartIcon,
      label: "Valores",
      title: "Confianza, calidad e innovación",
      text: "Trabajamos con compromiso, transparencia y respeto por la tierra. Cada decisión la tomamos pensando en nuestros productores.",
      accent: "green",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-[#0A130C]" data-testid="mvv-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16" data-reveal>
          <SectionLabel icon={MedalIcon} center>Nuestro Norte</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Lo que nos define como empresa
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6" data-reveal-stagger>
          {items.map((it, i) => {
            const Icon = it.icon;
            const isYellow = it.accent === "yellow";
            return (
              <div
                key={it.label}
                data-reveal="depth"
                className="card-3d group relative bg-gradient-to-br from-white/[0.04] to-white/[0.01] hover:from-white/[0.07] border border-white/[0.08] hover:border-agro-green/30 rounded-3xl p-8 transition-all duration-500"
              >
                <div className={`w-14 h-14 rounded-2xl ${isYellow ? "bg-agro-yellow/15" : "bg-agro-green/15"} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={28} weight="fill" className={isYellow ? "text-agro-yellow" : "text-agro-green"} />
                </div>
                <div className={`text-[11px] uppercase tracking-[0.2em] ${isYellow ? "text-agro-yellow" : "text-agro-green"} font-semibold mb-2`}>{it.label}</div>
                <h3 className="font-heading text-xl font-semibold text-white mb-4 leading-snug">{it.title}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{it.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// PRODUCTS
// ============================================================
function Products({ cfg }) {
  const list = cfg?.products || [
    { title: "Semillas de Hortalizas", desc: "Variedades certificadas de alto rendimiento...", image: PHOTO.prod_seeds, icon: "plant" },
    { title: "Agroquímicos", desc: "Insecticidas, fungicidas y herbicidas...", image: PHOTO.prod_chem, icon: "flask" },
    { title: "Fertilizantes", desc: "Fórmulas balanceadas...", image: PHOTO.prod_fert, icon: "drop" },
    { title: "Bombas Fumigadoras", desc: "Equipos manuales y de motor...", image: PHOTO.prod_pump, icon: "spray" },
    { title: "Enmiendas y Sustratos", desc: "Cal agrícola, yeso, abonos...", image: PHOTO.prod_enm, icon: "mountains" },
    { title: "Soluciones Especializadas", desc: "Asesoría agronómica...", image: PHOTO.prod_special, icon: "package" },
  ];
  return (
    <section id="productos" className="py-24 md:py-32 bg-[#0A0F0B]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16" data-reveal>
          <SectionLabel icon={StorefrontIcon} center>Productos y Soluciones</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl mx-auto mb-3">
            Todo lo que tu cultivo necesita, en un solo lugar
          </h2>
          <span className="ag-swoosh" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-reveal-stagger>
          {list.map((c, i) => {
            const Icon = ICON_MAP[c.icon] || PackageIcon;
            return (
              <div
                key={c.title}
                data-reveal="depth"
                data-testid={`product-card-${i}`}
                className="card-3d group relative bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-agro-green/30 rounded-3xl overflow-hidden transition-all duration-500"
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0B] via-[#0A0F0B]/40 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-agro-green/90 backdrop-blur-sm flex items-center justify-center">
                    <Icon size={22} weight="fill" className="text-black" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">{c.title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed mb-4">{c.desc}</p>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-agro-green group-hover:gap-2.5 transition-all">
                    Ver soluciones
                    <ArrowRightIcon size={12} weight="bold" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// TESTIMONIALS (nueva sección)
// ============================================================
function Testimonials({ cfg }) {
  const list = cfg?.testimonials || [];
  if (list.length === 0) return null;
  return (
    <section className="py-24 md:py-32 bg-[#0A130C] relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 pointer-events-none opacity-20"
           style={{ backgroundImage: "radial-gradient(circle at 80% 20%, rgba(250,210,44,0.3), transparent 50%), radial-gradient(circle at 20% 80%, rgba(2,191,68,0.25), transparent 50%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16" data-reveal>
          <SectionLabel icon={QuotesIcon} center>Testimoniales</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
            Lo que dicen <span className="text-agro-green">nuestros productores</span>
          </h2>
          <span className="ag-swoosh" />
        </div>
        <div className="grid md:grid-cols-3 gap-6" data-reveal-stagger>
          {list.map((t, i) => (
            <div
              key={i}
              data-reveal="depth"
              data-testid={`testimonial-${i}`}
              className="card-3d relative bg-white rounded-3xl p-7 shadow-2xl flex flex-col"
            >
              <div className="absolute -top-5 left-7 w-12 h-12 rounded-full bg-agro-yellow flex items-center justify-center shadow-lg">
                <QuotesIcon size={20} weight="fill" className="text-black" />
              </div>
              <div className="flex items-center gap-3 mb-4 pt-4">
                {t.avatar ? (
                  <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-agro-yellow" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-agro-green text-black font-bold flex items-center justify-center ring-2 ring-agro-yellow">
                    {(t.name || "?").split(" ").slice(0, 2).map((s) => s[0]).join("")}
                  </div>
                )}
                <div>
                  <div className="font-heading text-sm font-semibold text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.role}</div>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed flex-1">“{t.text}”</p>
              <div className="mt-5 pt-4 border-t border-gray-100 -mx-7 -mb-7 px-7 pb-5 bg-agro-green/95 rounded-b-3xl">
                <div className="text-xs font-semibold text-black uppercase tracking-wider">Cliente AGROPORT</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// COVERAGE (mapa simple de Honduras)
// ============================================================
function Coverage() {
  return (
    <section id="cobertura" className="relative py-24 md:py-32 bg-[#0A130C] overflow-hidden" data-testid="coverage-section">
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 70% 50%, rgba(2,191,68,0.35), transparent 60%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5" data-reveal="fade-right" data-reveal-duration="1000">
          <SectionLabel icon={MapPinIcon}>Cobertura Nacional</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Llegamos a cada productor en <span className="text-agro-green">todo Honduras</span>
          </h2>
          <p className="text-base text-white/70 leading-relaxed mb-8">
            Con dos sucursales estratégicas y un equipo de Representantes Técnico-Comerciales (RTC),
            cubrimos los 18 departamentos del país con asesoría directa en campo.
          </p>
          <div className="space-y-4">
            <BranchCard city="Choluteca" address="Barrio El Centro, Choluteca, Honduras" phone="+504 9914-9976" />
            <BranchCard city="San Marcos de Colón" address="Choluteca, Honduras" phone="+504 3178-1500" featured />
          </div>
        </div>
        <div className="lg:col-span-7" data-reveal="scale-in" data-reveal-delay="200" data-reveal-duration="1100">
          <HondurasMap />
        </div>
      </div>
    </section>
  );
}

function BranchCard({ city, address, phone, featured }) {
  return (
    <div className={`flex items-center gap-4 p-5 rounded-2xl border transition-all hover:scale-[1.01] ${featured ? "bg-agro-green/10 border-agro-green/30" : "bg-white/[0.03] border-white/[0.08]"}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${featured ? "bg-agro-green" : "bg-white/10"}`}>
        <MapPinIcon size={22} weight="fill" className={featured ? "text-black" : "text-agro-green"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <h4 className="font-heading text-base font-semibold text-white">{city}</h4>
          {featured && <span className="text-[10px] uppercase tracking-wider text-agro-green font-semibold">Casa Matriz</span>}
        </div>
        <p className="text-xs text-white/55 mt-0.5">{address}</p>
        <a href={`tel:${phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-sm text-agro-green hover:text-agro-yellow font-medium mt-1">
          <PhoneIcon size={12} weight="fill" /> {phone}
        </a>
      </div>
    </div>
  );
}

// Mapa REAL de Honduras — silueta vectorial fiel a la geografía del país.
// Path generado a partir de la frontera real (simplificada). Coords lat/lon → SVG.
// ViewBox 0-100 x 0-60. Origen (0,0) = NW, (100,60) = SE.
function HondurasMap() {
  // 18 departamentos con coords reales (lat/lon → x/y)
  // x = (lon + 89.4) / 6.3 * 100  ;  y = (16.5 - lat) / 3.6 * 60
  const departments = [
    { name: "Cortés (SPS)",            x: 21.4, y: 15.8, big: true },
    { name: "Atlántida (La Ceiba)",    x: 41.3, y: 16.7 },
    { name: "Colón (Trujillo)",        x: 61.9, y: 16.7 },
    { name: "Gracias a Dios (Puerto Lempira)", x: 81.0, y: 21.7, big: true },
    { name: "Yoro",                    x: 35.7, y: 24.2 },
    { name: "Olancho (Juticalpa)",     x: 61.9, y: 25.8, big: true },
    { name: "Santa Bárbara",           x: 19.0, y: 25.0 },
    { name: "Copán (Santa Rosa)",      x: 7.1,  y: 27.5 },
    { name: "Comayagua",               x: 27.8, y: 32.5 },
    { name: "Lempira (Gracias)",       x: 13.5, y: 32.5 },
    { name: "Ocotepeque",              x: 4.8,  y: 34.2 },
    { name: "Intibucá (La Esperanza)", x: 19.8, y: 36.7 },
    { name: "La Paz",                  x: 27.0, y: 36.7 },
    { name: "Francisco Morazán (TGU)", x: 34.9, y: 40.0, big: true },
    { name: "El Paraíso (Yuscarán)",   x: 45.2, y: 44.2 },
    { name: "Valle (Nacaome)",         x: 29.4, y: 50.0 },
    { name: "Choluteca",               x: 32.8, y: 54.0, primary: true },
    { name: "Islas de la Bahía",       x: 46.0, y: 4.0 },
  ];

  // Contorno realista de Honduras — frontera norte (Caribe), este (Mosquitia/Río Coco),
  // sur (Pacífico-Fonseca) y oeste (Guatemala/El Salvador).
  // Cada punto corresponde a una coordenada geográfica real simplificada.
  const hondurasPath = `
    M 13.5 14.5
    L 18.0 13.2
    L 22.0 11.5
    L 26.5 11.8
    L 31.0 11.0
    L 35.5 10.5
    L 40.0 10.3
    L 44.0 10.0
    L 48.5 9.5
    L 52.5 9.3
    L 56.0 9.0
    L 60.5 8.8
    L 64.5 9.5
    L 68.0 10.2
    L 71.5 11.0
    L 75.0 11.8
    L 78.5 12.5
    L 82.0 13.5
    L 85.5 15.0
    L 88.5 17.5
    L 91.0 20.5
    L 93.0 23.5
    L 94.5 27.0
    L 94.8 30.5
    L 94.0 33.5
    L 91.5 35.5
    L 88.0 36.5
    L 84.0 37.0
    L 80.0 37.2
    L 76.0 37.5
    L 72.0 38.0
    L 68.0 38.8
    L 64.5 39.5
    L 61.0 40.0
    L 57.5 40.5
    L 54.0 41.2
    L 51.0 42.0
    L 48.5 43.0
    L 46.5 44.5
    L 45.0 46.5
    L 43.5 48.5
    L 42.0 50.5
    L 40.0 52.0
    L 37.5 53.0
    L 35.0 54.0
    L 33.0 55.5
    L 31.5 56.8
    L 30.0 56.5
    L 29.0 54.5
    L 28.5 52.5
    L 28.0 50.0
    L 27.0 47.5
    L 25.5 45.0
    L 23.5 43.5
    L 21.0 42.5
    L 18.0 41.5
    L 14.5 40.0
    L 11.5 38.5
    L 8.5 37.0
    L 5.5 35.0
    L 3.0 32.5
    L 1.5 30.0
    L 0.8 27.0
    L 1.0 24.0
    L 1.8 21.0
    L 3.0 18.5
    L 5.0 16.5
    L 7.5 15.2
    L 10.5 14.5
    L 13.5 14.5
    Z
  `;

  // Líneas de departamentos internas (mosaico estilizado)
  const internalLines = [
    "M 27 11 L 27 35 L 22 42",
    "M 42 10 L 44 32 L 47 42",
    "M 60 9 L 60 36 L 56 41",
    "M 75 11.5 L 75 37",
    "M 5 16 L 50 9.5",
    "M 0.8 27 L 95 27",
    "M 1.5 30 L 95 30",
    "M 90 20 L 92 33",
  ];

  return (
    <div className="relative aspect-[16/11] bg-white/[0.02] border border-white/[0.06] rounded-3xl p-6 overflow-hidden">
      <svg viewBox="0 0 100 60" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="hondGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(2,191,68,0.28)" />
            <stop offset="55%" stopColor="rgba(2,191,68,0.13)" />
            <stop offset="100%" stopColor="rgba(2,191,68,0.05)" />
          </linearGradient>
          <linearGradient id="hondStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(2,191,68,0.85)" />
            <stop offset="100%" stopColor="rgba(2,191,68,0.5)" />
          </linearGradient>
          <radialGradient id="hondGlowGrad" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgba(2,191,68,0.18)" />
            <stop offset="100%" stopColor="rgba(2,191,68,0)" />
          </radialGradient>
        </defs>

        {/* Halo de fondo */}
        <ellipse cx="48" cy="28" rx="55" ry="28" fill="url(#hondGlowGrad)" />

        {/* Silueta de Honduras */}
        <path
          d={hondurasPath}
          fill="url(#hondGrad)"
          stroke="url(#hondStroke)"
          strokeWidth="0.45"
          strokeLinejoin="round"
        />

        {/* Líneas departamentales internas — sutiles */}
        <g stroke="rgba(2,191,68,0.20)" strokeWidth="0.15" fill="none" strokeDasharray="0.4,0.6">
          {internalLines.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </g>

        {/* Islas de la Bahía — Utila, Roatán, Guanaja */}
        <g fill="rgba(2,191,68,0.55)" stroke="rgba(2,191,68,0.75)" strokeWidth="0.18">
          <ellipse cx="42" cy="4.2" rx="1.8" ry="0.55" />
          <ellipse cx="47.5" cy="3.6" rx="3.2" ry="0.85" />
          <ellipse cx="53.5" cy="4.4" rx="2.0" ry="0.55" />
        </g>

        {/* Río Patuca (decorativo) */}
        <path
          d="M 62 28 Q 70 30, 78 32 Q 84 34, 88 35.5"
          fill="none"
          stroke="rgba(120,180,255,0.18)"
          strokeWidth="0.3"
          strokeLinecap="round"
        />

        {/* Puntos departamentales */}
        {departments.map((p) => (
          <g key={p.name}>
            {p.primary ? (
              <>
                <circle cx={p.x} cy={p.y} r="3.5" fill="none" stroke="#FAD22C" strokeWidth="0.2" opacity="0.5">
                  <animate attributeName="r" from="1.6" to="5.5" dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.7" to="0" dur="2.4s" repeatCount="indefinite" />
                </circle>
                <circle cx={p.x} cy={p.y} r="1.7" fill="#FAD22C" />
                <circle cx={p.x} cy={p.y} r="0.6" fill="#0A0F0B" />
              </>
            ) : (
              <circle
                cx={p.x}
                cy={p.y}
                r={p.big ? "1.0" : "0.7"}
                fill={p.big ? "#02BF44" : "rgba(2,191,68,0.75)"}
                stroke="rgba(0,0,0,0.4)"
                strokeWidth="0.1"
              >
                <title>{p.name}</title>
              </circle>
            )}
          </g>
        ))}

        {/* Etiquetas de ciudades clave */}
        <g fill="rgba(255,255,255,0.42)" fontSize="1.7" fontFamily="sans-serif" fontWeight="500">
          <text x="36.5" y="40.0" textAnchor="start" dx="2">Tegucigalpa</text>
          <text x="22.4" y="15.5" textAnchor="start" dx="2">San Pedro Sula</text>
          <text x="83" y="22" textAnchor="start" dx="2" fill="rgba(255,255,255,0.32)">Puerto Lempira</text>
          <text x="63" y="17" textAnchor="start" dx="2" fill="rgba(255,255,255,0.28)">Trujillo</text>
          <text x="34.5" y="54" textAnchor="start" dx="2" fill="rgba(250,210,44,0.95)" fontWeight="700">Choluteca</text>
        </g>
      </svg>

      <div className="absolute top-4 right-4 bg-[#0A0F0B]/85 backdrop-blur-md border border-white/10 rounded-xl px-3 py-2">
        <div className="flex items-center gap-2 text-[10px] text-white/75 mb-1">
          <span className="w-2 h-2 rounded-full bg-agro-yellow shadow-[0_0_8px_rgba(250,210,44,0.6)]" /> Casa matriz
        </div>
        <div className="flex items-center gap-2 text-[10px] text-white/75">
          <span className="w-2 h-2 rounded-full bg-agro-green" /> Departamentos con cobertura
        </div>
      </div>
      <div className="absolute bottom-4 left-4 text-white/40 text-[10px] uppercase tracking-wider">
        18 departamentos · Cobertura nacional
      </div>
    </div>
  );
}

// ============================================================
// STATS (indicadores de confianza)
// ============================================================
function Stats({ cfg }) {
  const stats = (cfg?.stats || []).map((s) => ({
    icon: ClockIcon, to: s.value, suffix: s.suffix, label: s.label,
  }));
  const fallback = [
    { icon: ClockIcon, to: 15, suffix: "+", label: "Años de experiencia" },
    { icon: UsersThreeIcon, to: 1500, suffix: "+", label: "Productores atendidos" },
    { icon: PackageIcon, to: 800, suffix: "+", label: "Productos comercializados" },
    { icon: TrophyIcon, to: 18, suffix: "", label: "Departamentos con cobertura" },
  ];
  const final = stats.length > 0 ? stats : fallback;
  // Asignar iconos rotando
  const icons = [ClockIcon, UsersThreeIcon, PackageIcon, TrophyIcon];
  return (
    <section className="py-20 bg-agro-green relative overflow-hidden" data-testid="stats-section">
      <div className="absolute inset-0 opacity-30 mix-blend-overlay"
           style={{ backgroundImage: "radial-gradient(circle at 30% 50%, rgba(255,255,255,0.4), transparent 40%), radial-gradient(circle at 70% 50%, rgba(0,0,0,0.2), transparent 50%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-reveal-stagger>
          {final.map((s, i) => {
            const Icon = icons[i % icons.length];
            return (
              <div key={s.label} className="text-center" data-reveal="scale-in">
                <div className="w-14 h-14 rounded-2xl bg-black/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                  <Icon size={28} weight="fill" className="text-black" />
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-black mb-2">
                  <CountUp to={s.to} suffix={s.suffix} />
                </div>
                <div className="text-sm font-medium text-black/75 uppercase tracking-wider">{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// GALLERY
// ============================================================
function Gallery({ cfg }) {
  const imgs = cfg?.gallery && cfg.gallery.length > 0 ? cfg.gallery : PHOTO.gallery;
  return (
    <section id="galeria" className="py-24 md:py-32 bg-[#0A0F0B]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16" data-reveal>
          <SectionLabel icon={EyeIcon} center>Galería</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl mx-auto">
            La historia se cuenta con <span className="text-agro-green">imágenes</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-reveal-stagger>
          {imgs.map((src, i) => (
            <div
              key={i}
              data-reveal="blur-in"
              className={`group relative overflow-hidden rounded-2xl ${i === 0 || i === 5 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-square"}`}
            >
              <img src={src} alt={`Galería ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-agro-green/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// CONTACT
// ============================================================
function Contact({ cfg }) {
  const c = cfg?.contact || {};
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", mensaje: "" });
  const [sending, setSending] = useState(false);
  const submitRef = useMagnetic({ strength: 0.2 });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completá los campos requeridos");
      return;
    }
    setSending(true);
    try {
      // Endpoint público de contacto (puede no existir aún — usamos fallback a mailto)
      await api.post("/contact", form).catch(() => {
        // Fallback: abrir mailto
        const subject = encodeURIComponent(`Contacto desde web — ${form.nombre}`);
        const body = encodeURIComponent(`Nombre: ${form.nombre}\nEmail: ${form.email}\nTeléfono: ${form.telefono}\n\nMensaje:\n${form.mensaje}`);
        window.open(`mailto:servicioalcliente@agroporthonduras.com?subject=${subject}&body=${body}`, "_blank");
      });
      toast.success("Mensaje enviado. Pronto te contactaremos.");
      setForm({ nombre: "", email: "", telefono: "", mensaje: "" });
    } finally { setSending(false); }
  };

  return (
    <section id="contacto" className="py-24 md:py-32 bg-[#0A130C]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12" data-reveal>
          <SectionLabel icon={EnvelopeSimpleIcon} center>Contáctanos</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Hablemos del próximo ciclo de tu cultivo
          </h2>
        </div>
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4" data-reveal="fade-right" data-reveal-duration="1000">
            <ContactItem icon={WhatsappLogoIcon} title="WhatsApp Choluteca" value={c.phone_choluteca || "+504 9914-9976"}
                         href={c.whatsapp_choluteca || "https://wa.me/50499149976"} accent="green" />
            <ContactItem icon={WhatsappLogoIcon} title="WhatsApp San Marcos" value={c.phone_san_marcos || "+504 3178-1500"}
                         href={c.whatsapp_san_marcos || "https://wa.me/50431781500"} accent="green" />
            <ContactItem icon={EnvelopeSimpleIcon} title="Correo electrónico" value={c.email || "servicioalcliente@agroporthonduras.com"}
                         href={`mailto:${c.email || "servicioalcliente@agroporthonduras.com"}`} />
            <ContactItem icon={ClockIcon} title="Horarios de atención"
                         value={c.hours || "Lun – Vie: 7:30 a.m – 5:00 p.m · Sáb: 8:00 a.m – 12:00 p.m"} />
            <ContactItem icon={MapPinIcon} title="Casa Matriz"
                         value={c.address || "San Marcos de Colón, Choluteca, Honduras"} />
          </div>
          <form onSubmit={submit} className="lg:col-span-7 glass-premium rounded-3xl p-6 md:p-8 space-y-4" data-reveal="fade-left" data-reveal-delay="150" data-reveal-duration="1000" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nombre completo" required>
                <input
                  data-testid="contact-input-nombre"
                  type="text"
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full bg-white/[0.04] border border-white/10 focus:border-agro-green rounded-xl px-4 py-3 text-sm placeholder:text-white/30 outline-none transition-colors"
                  placeholder="Tu nombre"
                />
              </Field>
              <Field label="Correo electrónico" required>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white/[0.04] border border-white/10 focus:border-agro-green rounded-xl px-4 py-3 text-sm placeholder:text-white/30 outline-none transition-colors"
                  placeholder="tucorreo@ejemplo.com"
                />
              </Field>
            </div>
            <Field label="Teléfono / WhatsApp">
              <input
                data-testid="contact-input-telefono"
                type="tel"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="w-full bg-white/[0.04] border border-white/10 focus:border-agro-green rounded-xl px-4 py-3 text-sm placeholder:text-white/30 outline-none transition-colors"
                placeholder="+504 ..."
              />
            </Field>
            <Field label="¿En qué te podemos ayudar?" required>
              <textarea
                data-testid="contact-input-mensaje"
                required
                rows={5}
                value={form.mensaje}
                onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                className="w-full bg-white/[0.04] border border-white/10 focus:border-agro-green rounded-xl px-4 py-3 text-sm placeholder:text-white/30 outline-none transition-colors resize-none"
                placeholder="Cuéntanos qué cultivo manejas y cómo podemos apoyarte…"
              />
            </Field>
            <button
              ref={submitRef}
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="magnetic-btn magnetic-btn-shimmer w-full md:w-auto inline-flex items-center justify-center gap-2.5 bg-agro-green hover:bg-agro-green-hover disabled:opacity-60 text-black font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
            >
              {sending ? "Enviando…" : "Enviar mensaje"}
              <ArrowRightIcon size={14} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, title, value, href, accent }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
             className="flex items-start gap-4 p-5 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-agro-green/30 rounded-2xl transition-all">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accent === "green" ? "bg-agro-green/15 text-agro-green" : "bg-white/10 text-white/80"}`}>
        <Icon size={20} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1">{title}</div>
        <div className="text-sm text-white font-medium break-words">{value}</div>
      </div>
    </Wrapper>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-white/70 mb-1.5 block">
        {label} {required && <span className="text-agro-green">*</span>}
      </span>
      {children}
    </label>
  );
}

// ============================================================
// FOOTER
// ============================================================
function Footer({ cfg }) {
  const c = cfg?.contact || {};
  const f = cfg?.footer || {};
  return (
    <footer className="bg-[#050807] border-t border-white/5 pt-16 pb-8" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <img src="/agroport-logo-green.png" alt="AGROPORT" className="h-12 w-auto mb-4" />
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              {f.description || "Soluciones agrícolas integrales para productores hondureños. Semillas, fertilizantes, agroquímicos y asesoría técnica con cobertura nacional."}
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: FacebookLogoIcon, href: f.facebook || "#", label: "Facebook" },
                { Icon: InstagramLogoIcon, href: f.instagram || "#", label: "Instagram" },
                { Icon: WhatsappLogoIcon, href: c.whatsapp_san_marcos || "https://wa.me/50431781500", label: "WhatsApp" },
                { Icon: LinkedinLogoIcon, href: f.linkedin || "#", label: "LinkedIn" },
              ].map(({ Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                   className="w-10 h-10 rounded-full bg-white/[0.04] hover:bg-agro-green border border-white/10 hover:border-agro-green text-white/70 hover:text-black flex items-center justify-center transition-all">
                  <Icon size={16} weight="fill" />
                </a>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-heading text-sm font-semibold text-white mb-4 uppercase tracking-wider">Enlaces</h4>
            <ul className="space-y-2.5">
              {["Nosotros", "Productos", "Cobertura", "Galería", "Contacto"].map((l) => (
                <li key={l}>
                  <a href={`#${l.toLowerCase()}`} className="text-sm text-white/60 hover:text-agro-green transition-colors">{l}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="font-heading text-sm font-semibold text-white mb-4 uppercase tracking-wider">Contacto</h4>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex items-start gap-2.5"><PhoneIcon size={14} className="text-agro-green mt-0.5 flex-shrink-0" /> Choluteca: {c.phone_choluteca || "+504 9914-9976"}</li>
              <li className="flex items-start gap-2.5"><PhoneIcon size={14} className="text-agro-green mt-0.5 flex-shrink-0" /> San Marcos: {c.phone_san_marcos || "+504 3178-1500"}</li>
              <li className="flex items-start gap-2.5"><EnvelopeSimpleIcon size={14} className="text-agro-green mt-0.5 flex-shrink-0" /> {c.email || "servicioalcliente@agroporthonduras.com"}</li>
              <li className="flex items-start gap-2.5"><MapPinIcon size={14} className="text-agro-green mt-0.5 flex-shrink-0" /> {c.address || "San Marcos de Colón, Choluteca, Honduras"}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} AGROPORT Honduras. Todos los derechos reservados.
          </div>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/login" className="text-white/60 hover:text-agro-green" data-testid="footer-login-link">Iniciar Sesión</Link>
            <a href="#contacto" className="text-white/60 hover:text-agro-green">Aviso de privacidad</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// Helpers
// ============================================================
function SectionLabel({ icon: Icon, children, center }) {
  return (
    <div className={`inline-flex items-center gap-2 bg-agro-green/10 border border-agro-green/25 rounded-full px-3 py-1.5 mb-5 ${center ? "" : ""}`}>
      <Icon size={12} weight="fill" className="text-agro-green" />
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-agro-green">{children}</span>
    </div>
  );
}
